import {
  decimal,
  formatMxn,
  maxDecimal,
  minDecimal,
  money,
  nonNegative,
  precise,
} from "./money";
import {
  frequencyDays,
  fiscalSourcesFor,
  LEGAL_SOURCES,
  parametersFor,
} from "./parameters";
import { calculatePeriodIncomeTax } from "./tax";
import type {
  CalculationResult,
  PayrollInput,
  ResultLine,
  WageInput,
} from "./types";

export function dailySalary(wage: WageInput): number {
  const days = frequencyDays[wage.frequency];
  return money(nonNegative(wage.amount).div(days));
}

export function integrationFactor(
  aguinaldoDays: number,
  vacationDays: number,
  vacationPremiumRate: number,
): number {
  return precise(
    decimal(1)
      .plus(decimal(aguinaldoDays).div(365))
      .plus(
        decimal(vacationDays).times(vacationPremiumRate).div(365),
      ),
  );
}

function ceavRate(sbcDaily: number, minimumWage: number, uma: number, rates: ReturnType<typeof parametersFor>["ceavEmployerRates"]): number {
  if (sbcDaily <= minimumWage) return 0.0315;
  const umaMultiple = sbcDaily / uma;
  return rates.find(({ maxUma }) => umaMultiple <= maxUma)?.rate ?? rates[rates.length - 1].rate;
}

export function calculateSocialSecurity(
  sbcDaily: number,
  days: number,
  minimumWage: number,
  umaDaily: number,
  riskPremiumRate: number,
  ceavRates: ReturnType<typeof parametersFor>["ceavEmployerRates"],
  workerEarnsMinimum = false,
): {
  employee: number;
  employer: number;
  employeeBreakdown: Record<string, number>;
  employerBreakdown: Record<string, number>;
} {
  const sbc = decimal(sbcDaily);
  const periodDays = decimal(days);
  const base = sbc.times(periodDays);
  const excess = maxDecimal(
    0,
    sbc.minus(decimal(umaDaily).times(3)),
  ).times(periodDays);
  const baseEmployeeBreakdown = {
    enfermedadesExcedente: money(excess.times(0.004)),
    enfermedadesDinero: money(base.times(0.0025)),
    gastosMedicosPensionados: money(base.times(0.00375)),
    invalidezVida: money(base.times(0.00625)),
    cesantiaVejez: money(base.times(0.01125)),
  };
  const absorbedEmployee = workerEarnsMinimum
    ? money(
        Object.values(baseEmployeeBreakdown).reduce(
          (sum, value) => sum + value,
          0,
        ),
      )
    : 0;
  const employeeBreakdown = workerEarnsMinimum
    ? Object.fromEntries(
        Object.keys(baseEmployeeBreakdown).map((key) => [key, 0]),
      )
    : baseEmployeeBreakdown;

  const employerBreakdown = {
    enfermedadesCuotaFija: money(
      decimal(umaDaily).times(periodDays).times(0.204),
    ),
    enfermedadesExcedente: money(excess.times(0.011)),
    enfermedadesDinero: money(base.times(0.007)),
    gastosMedicosPensionados: money(base.times(0.0105)),
    riesgoTrabajo: money(base.times(nonNegative(riskPremiumRate).div(100))),
    invalidezVida: money(base.times(0.0175)),
    guarderiasPrestaciones: money(base.times(0.01)),
    retiro: money(base.times(0.02)),
    cesantiaVejez: money(
      base.times(ceavRate(sbcDaily, minimumWage, umaDaily, ceavRates)),
    ),
    infonavit: money(base.times(0.05)),
    cuotaObreraAbsorbida: absorbedEmployee,
  };

  return {
    employee: money(
      Object.values(employeeBreakdown).reduce((sum, value) => sum + value, 0),
    ),
    employer: money(
      Object.values(employerBreakdown).reduce((sum, value) => sum + value, 0),
    ),
    employeeBreakdown,
    employerBreakdown,
  };
}

export function calculatePayroll(input: PayrollInput): CalculationResult {
  const params = parametersFor(input.payDate);
  const daily = dailySalary(input.wage);
  if (daily <= 0) throw new Error("El salario debe ser mayor a cero.");

  const standardDays = frequencyDays[input.wage.frequency];
  const payDate = new Date(`${input.payDate}T00:00:00Z`);
  const calendarMonthDays = new Date(
    Date.UTC(payDate.getUTCFullYear(), payDate.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const days =
    input.daysInPeriod && input.daysInPeriod > 0
      ? input.daysInPeriod
      : input.wage.frequency === "monthly"
        ? calendarMonthDays
        : standardDays;
  const monthly = input.wage.frequency === "monthly" && !input.daysInPeriod;
  const basePay = money(
    input.wage.frequency === "monthly" && monthly
      ? input.wage.amount
      : decimal(daily).times(days),
  );

  const aguinaldoDays = Math.max(15, input.aguinaldoDays ?? 15);
  const vacationDays = Math.max(12, input.vacationDaysPerYear ?? 12);
  const vacationPremiumRate = Math.max(
    0.25,
    input.vacationPremiumRate ?? 0.25,
  );
  const factor = integrationFactor(
    aguinaldoDays,
    vacationDays,
    vacationPremiumRate,
  );
  const integratedDaily = money(decimal(daily).times(factor));
  const sbcDaily = money(
    minDecimal(
      maxDecimal(integratedDaily, params.minimumWage[input.zone]),
      decimal(params.umaDaily).times(25),
    ),
  );

  const overtimeHours = Math.max(0, input.overtimeHours ?? 0);
  const doubleHours = Math.min(
    overtimeHours,
    params.overtimeDoubleWeeklyLimit,
  );
  const tripleHours = Math.max(0, overtimeHours - doubleHours);
  const hourly = decimal(daily).div(8);
  const overtimeDouble = money(hourly.times(doubleHours).times(2));
  const overtimeTriple = money(hourly.times(tripleHours).times(3));
  const sundayPremium = money(
    decimal(daily).times(0.25).times(input.sundayShifts ?? 0),
  );
  const restDayPay = money(
    decimal(daily).times(2).times(input.holidayOrRestDays ?? 0),
  );
  const otherTaxable = money(nonNegative(input.otherTaxableIncome));
  const otherNonTaxable = money(nonNegative(input.otherNonTaxableIncome));
  const gross = money(
    decimal(basePay)
      .plus(overtimeDouble)
      .plus(overtimeTriple)
      .plus(sundayPremium)
      .plus(restDayPay)
      .plus(otherTaxable)
      .plus(otherNonTaxable),
  );

  const earnsMinimum = daily <= params.minimumWage[input.zone];
  const overtimeExempt = earnsMinimum
    ? overtimeDouble
    : money(
        minDecimal(
          decimal(overtimeDouble).times(0.5),
          decimal(params.umaDaily).times(5),
        ),
      );
  const sundayExempt = money(
    minDecimal(
      sundayPremium,
      decimal(params.umaDaily).times(input.sundayShifts ?? 0),
    ),
  );
  const exempt = money(
    decimal(overtimeExempt).plus(sundayExempt).plus(otherNonTaxable),
  );
  const taxable = money(decimal(gross).minus(exempt));
  const tax = calculatePeriodIncomeTax(taxable, days, params, {
    monthly,
    subsidyEligible: true,
  });
  const socialSecurity = calculateSocialSecurity(
    sbcDaily,
    days,
    params.minimumWage[input.zone],
    params.umaDaily,
    input.riskPremiumRate ?? 0.54355,
    params.ceavEmployerRates,
    earnsMinimum,
  );
  const payrollTaxRate = Math.max(0, input.payrollTaxRate ?? 0);
  const payrollTax = money(decimal(gross).times(payrollTaxRate).div(100));
  const otherDeductions = money(
    decimal(input.infonavitWithholding ?? 0).plus(
      input.otherDeductions ?? 0,
    ),
  );
  const net = money(
    decimal(gross)
      .minus(tax.isrWithheld)
      .minus(socialSecurity.employee)
      .minus(otherDeductions),
  );
  const employerCost = money(
    decimal(gross).plus(socialSecurity.employer).plus(payrollTax),
  );

  const lines: ResultLine[] = [
    {
      key: "base-pay",
      label: "Sueldo del periodo",
      amount: basePay,
      category: "earning",
    },
    ...(overtimeDouble
      ? [
          {
            key: "overtime-double",
            label: `Horas extra dobles (${doubleHours})`,
            amount: overtimeDouble,
            category: "earning" as const,
            legalBasis: "LFT arts. 66 y 68",
          },
        ]
      : []),
    ...(overtimeTriple
      ? [
          {
            key: "overtime-triple",
            label: `Horas extra triples (${tripleHours})`,
            amount: overtimeTriple,
            category: "earning" as const,
            legalBasis: "LFT art. 68",
          },
        ]
      : []),
    ...(sundayPremium
      ? [
          {
            key: "sunday",
            label: "Prima dominical",
            amount: sundayPremium,
            category: "benefit" as const,
            legalBasis: "LFT art. 71",
          },
        ]
      : []),
    ...(restDayPay
      ? [
          {
            key: "rest-day",
            label: "Pago adicional por descanso obligatorio laborado",
            amount: restDayPay,
            category: "benefit" as const,
            legalBasis: "LFT arts. 73 y 75",
          },
        ]
      : []),
    {
      key: "isr",
      label: "ISR estimado a retener",
      amount: -tax.isrWithheld,
      category: "tax",
      legalBasis: "LISR art. 96; Anexo 8 RMF 2026",
      note:
        tax.subsidyApplied > 0
          ? `Incluye subsidio aplicado de ${formatMxn(tax.subsidyApplied)}.`
          : undefined,
    },
    {
      key: "imss-worker",
      label: "Cuotas obreras IMSS estimadas",
      amount: -socialSecurity.employee,
      category: "deduction",
      legalBasis: "LSS arts. 25, 106, 107, 147 y 168",
    },
    ...(otherDeductions
      ? [
          {
            key: "other-deductions",
            label: "INFONAVIT y otras deducciones capturadas",
            amount: -otherDeductions,
            category: "deduction" as const,
          },
        ]
      : []),
    {
      key: "employer-social",
      label: "Cuotas patronales + INFONAVIT estimadas",
      amount: socialSecurity.employer,
      category: "employer",
      note: "No se descuenta a la persona trabajadora.",
    },
    ...(payrollTax
      ? [
          {
            key: "payroll-tax",
            label: `Impuesto estatal sobre nóminas (${payrollTaxRate.toFixed(2)}%)`,
            amount: payrollTax,
            category: "employer" as const,
            note: `${input.state || "Entidad no indicada"}; base estimada sobre percepciones brutas. Revisa exenciones y reglas locales.`,
          },
        ]
      : []),
  ];

  const warnings = [
    "Es una estimación informativa. Para timbrado o entero, valida incidencias, ausencias, variables bimestrales y redondeos en el sistema de nómina, SUA y CFDI.",
    ...(overtimeHours > params.overtimeAbsoluteWeeklyLimit
      ? [
          `Se capturaron más de ${params.overtimeAbsoluteWeeklyLimit} horas extraordinarias; el excedente rebasa el máximo semanal de la LFT vigente en la fecha seleccionada.`,
        ]
      : []),
    ...(input.wage.frequency === "monthly" && input.daysInPeriod
      ? [
          "Se usó el número de días capturado en vez del periodo mensual completo.",
        ]
      : []),
    ...(!payrollTaxRate
      ? [
          "El costo patronal no incluye impuesto estatal sobre nóminas. Captura la tasa aplicable en modo avanzado para estimarlo.",
        ]
      : []),
  ];

  return {
    schemaVersion: 1,
    rulesVersion: params.version,
    calculatedAt: new Date().toISOString(),
    kind: "payroll",
    title: "Estimación de nómina",
    salary: {
      daily,
      integratedDaily,
      contributionBaseDaily: sbcDaily,
      periodDays: days,
    },
    totals: {
      gross,
      exempt,
      taxable,
      isr: tax.isrWithheld,
      socialSecurity: socialSecurity.employee,
      otherDeductions,
      net,
      employerCost,
      payrollTax,
    },
    lines,
    warnings,
    assumptions: [
      `Factor de integración ${factor.toFixed(6)} con ${aguinaldoDays} días de aguinaldo, ${vacationDays} días de vacaciones y ${(vacationPremiumRate * 100).toFixed(2)}% de prima.`,
      `Zona salarial: ${input.zone === "border" ? "ZLFN" : "general"}.`,
      "La cuota patronal de riesgos de trabajo usa la prima capturada; por defecto, la prima media de clase I.",
      ...(earnsMinimum
        ? [
            "La persona percibe salario mínimo; la cuota obrera de seguridad social se absorbe en el costo patronal conforme al artículo 36 LSS.",
          ]
        : []),
    ],
    citations: [
      LEGAL_SOURCES.lft,
      LEGAL_SOURCES.lss,
      ...fiscalSourcesFor(input.payDate),
      LEGAL_SOURCES.uma,
      LEGAL_SOURCES.minimumWage,
      LEGAL_SOURCES.sua,
    ],
  };
}
